import { Authenticated, Refine } from "@refinedev/core";
import { RefineThemes } from "@refinedev/antd";
import routerProvider, { CatchAllNavigate, NavigateToResource } from "@refinedev/react-router-v6";
import { ConfigProvider, Spin } from "antd";
import ruRU from "antd/locale/ru_RU";
import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";

import "@refinedev/antd/dist/reset.css";

// Sync — страница входа + DashboardPage (главный экран).
import { DashboardPage } from "./pages/dashboard";
import { LoginPage } from "./pages/login";
import { authProvider } from "./providers/auth-provider";
import { dataProvider } from "./providers/data-provider";
import { RoleGuard } from "./components/RoleGuard";
import { AdminLayout } from "./components/AdminLayout";
import { useAppRole } from "./hooks/use-app-role";
import { homePathFor } from "./lib/panel-access";
import { RESOURCES } from "./resources";

// Lazy — каждая страница становится отдельным chunk'ом. Главная сессия
// загрузит только Dashboard + auth, остальное — при первом переходе.
const LeadsKanbanPage = lazy(() =>
  import("./pages/leads/list").then((m) => ({ default: m.LeadsKanbanPage })),
);
const LeadsListPage = lazy(() =>
  import("./pages/leads/list-view").then((m) => ({ default: m.LeadsListPage })),
);
const LeadShowPage = lazy(() =>
  import("./pages/leads/show").then((m) => ({ default: m.LeadShowPage })),
);
const BroadcastsListPage = lazy(() =>
  import("./pages/broadcasts/list").then((m) => ({ default: m.BroadcastsListPage })),
);
const BroadcastShowPage = lazy(() =>
  import("./pages/broadcasts/show").then((m) => ({ default: m.BroadcastShowPage })),
);
const CohortsListPage = lazy(() =>
  import("./pages/cohorts/list").then((m) => ({ default: m.CohortsListPage })),
);
const CohortShowPage = lazy(() =>
  import("./pages/cohorts/show").then((m) => ({ default: m.CohortShowPage })),
);
const HomeworkQueuePage = lazy(() =>
  import("./pages/homework/queue").then((m) => ({ default: m.HomeworkQueuePage })),
);
const HomeworkShowPage = lazy(() =>
  import("./pages/homework/show").then((m) => ({ default: m.HomeworkShowPage })),
);
const AdminPaymentsListPage = lazy(() =>
  import("./pages/payments/list").then((m) => ({ default: m.AdminPaymentsListPage })),
);
const ModulesListPage = lazy(() =>
  import("./pages/modules/list").then((m) => ({ default: m.ModulesListPage })),
);
const ModuleEditPage = lazy(() =>
  import("./pages/modules/edit").then((m) => ({ default: m.ModuleEditPage })),
);
const ServiceRequestsListPage = lazy(() =>
  import("./pages/service-requests").then((m) => ({ default: m.ServiceRequestsListPage })),
);
const BankPartnersListPage = lazy(() =>
  import("./pages/bank-partners/list").then((m) => ({ default: m.BankPartnersListPage })),
);

function PageFallback(): JSX.Element {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
      <Spin size="large" />
    </div>
  );
}

/**
 * Index route that redirects mentors to /homework.
 * Admins see the dashboard as usual.
 * Must be rendered inside <Refine> so it can use Refine hooks.
 *
 * `fallback` задан явно, а не взят из `homePathFor`: домашняя страница админа —
 * это и есть `/`, дефолт увёл бы его в редирект сам на себя.
 */
function IndexRoute(): JSX.Element {
  return (
    <RoleGuard allow={["admin"]} fallback="/homework">
      <DashboardPage />
    </RoleGuard>
  );
}

/**
 * Неизвестный адрес внутри залогиненного layout'а — на домашнюю страницу роли:
 * админа на дашборд `/`, ментора на очередь ДЗ. Раньше catch-all уводил на
 * `/homework` вообще всех, включая админов.
 */
function HomeRedirect(): JSX.Element {
  const role = useAppRole();

  if (role === "loading") return <PageFallback />;
  return <Navigate to={homePathFor(role === "admin" ? "admin" : null)} replace />;
}

export const App = (): JSX.Element => (
  <BrowserRouter>
    <ConfigProvider locale={ruRU} theme={RefineThemes.Blue}>
      <Refine
        routerProvider={routerProvider}
        dataProvider={dataProvider}
        authProvider={authProvider}
        resources={RESOURCES}
        options={{
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
          disableTelemetry: true,
        }}
      >
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route
              element={
                <Authenticated key="protected" fallback={<CatchAllNavigate to="/login" />}>
                  <AdminLayout />
                </Authenticated>
              }
            >
              {/* IndexRoute redirects mentors to /homework, admins see dashboard. */}
              <Route index element={<IndexRoute />} />

              {/* SC-020 Kanban leads — admin only */}
              <Route
                path="/leads"
                element={
                  <RoleGuard allow={["admin"]}>
                    <LeadsKanbanPage />
                  </RoleGuard>
                }
              />
              {/* SC-020.list — табличный вид со всеми никнеймами — admin only */}
              <Route
                path="/leads/list"
                element={
                  <RoleGuard allow={["admin"]}>
                    <LeadsListPage />
                  </RoleGuard>
                }
              />
              {/* SC-021 Lead card — admin only */}
              <Route
                path="/leads/:id"
                element={
                  <RoleGuard allow={["admin"]}>
                    <LeadShowPage />
                  </RoleGuard>
                }
              />

              {/* SC-120 Broadcasts list — admin only */}
              <Route
                path="/broadcasts"
                element={
                  <RoleGuard allow={["admin"]}>
                    <BroadcastsListPage />
                  </RoleGuard>
                }
              />
              {/* SC-130 Broadcast card — admin only */}
              <Route
                path="/broadcasts/:id"
                element={
                  <RoleGuard allow={["admin"]}>
                    <BroadcastShowPage />
                  </RoleGuard>
                }
              />

              {/* SC-080 Cohorts list — admin only */}
              <Route
                path="/cohorts"
                element={
                  <RoleGuard allow={["admin"]}>
                    <CohortsListPage />
                  </RoleGuard>
                }
              />
              {/* SC-081 Cohort card — admin only */}
              <Route
                path="/cohorts/:id"
                element={
                  <RoleGuard allow={["admin"]}>
                    <CohortShowPage />
                  </RoleGuard>
                }
              />

              {/* SC-060 Homework queue — accessible to both admin and mentor */}
              <Route path="/homework" element={<HomeworkQueuePage />} />
              {/* SC-061 Homework card — accessible to both admin and mentor */}
              <Route path="/homework/:id" element={<HomeworkShowPage />} />

              {/* Admin payments — admin only */}
              <Route
                path="/payments"
                element={
                  <RoleGuard allow={["admin"]}>
                    <AdminPaymentsListPage />
                  </RoleGuard>
                }
              />

              {/* Заявки на партнёрские услуги — admin only */}
              <Route
                path="/service-requests"
                element={
                  <RoleGuard allow={["admin"]}>
                    <ServiceRequestsListPage />
                  </RoleGuard>
                }
              />

              {/* Банки-партнёры — admin only */}
              <Route
                path="/bank-partners"
                element={
                  <RoleGuard allow={["admin"]}>
                    <BankPartnersListPage />
                  </RoleGuard>
                }
              />

              {/* Modules — admin only */}
              <Route
                path="/modules"
                element={
                  <RoleGuard allow={["admin"]}>
                    <ModulesListPage />
                  </RoleGuard>
                }
              />
              <Route
                path="/modules/:id/edit"
                element={
                  <RoleGuard allow={["admin"]}>
                    <ModuleEditPage />
                  </RoleGuard>
                }
              />

              {/* Catch-all for unknown routes inside authenticated layout */}
              <Route path="*" element={<HomeRedirect />} />
            </Route>

            <Route
              element={
                <Authenticated key="auth-pages" fallback={<Outlet />}>
                  <NavigateToResource resource="dashboard" />
                </Authenticated>
              }
            >
              <Route path="/login" element={<LoginPage />} />
            </Route>
          </Routes>
        </Suspense>
      </Refine>
    </ConfigProvider>
  </BrowserRouter>
);
